import React from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import { verticals } from '../data/verticals';
import { verticalIcons, stock } from '../data/media';
import { MapPin, Phone, Mail, Globe, Clock, ArrowRight } from 'lucide-react';
import { FacebookIcon, InstagramIcon, TwitterIcon, YoutubeIcon } from './SocialIcons';
import { useIsMobile } from '../hooks/useIsMobile';

const colStagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};
const colItem: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// Explicit per-vertical domains, keyed by `id` — TLDs vary (.com / .in / .co.in)
// and some are already full URLs, some are bare domains. `v.website` on the
// vertical itself (in verticals.ts) still wins if it's set.
const VERTICAL_WEBSITES: Record<string, string> = {
  bms: 'reachmedinscansandlabs.com',
  rmc: 'https://reachmultispecialityclinic.com/',
  rhms: 'https://reachhomecare.in/',
  rp: 'reachpharma.in',
  rsp: 'reachsurgicals.co.in',
  academy: 'reachacademy.co.in',
  rsl: 'reachscansandlabs.com',
  impex: 'https://reachimpex.co/',
  gardens: 'reachgardens.in',
  rsa: 'reachsportsacademy.co.in',
};

const urlForVertical = (v: typeof verticals[number]): string => {
  const domain = v.website || VERTICAL_WEBSITES[v.id];
  if (!domain) return '#';
  return domain.startsWith('http') ? domain : `https://www.${domain}`;
};

const QUICK_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/groups', label: 'Our Verticals' },
  { to: '/announcements', label: 'Announcements' },
  { to: '/about', label: 'About Us' },
  { to: '/contact', label: 'Contact Us' },
];

const CONTACT_ITEMS = [
  { Icon: MapPin, text: 'Pillayar Koil Street, C. Pallavaram, Chennai – 600043' },
  { Icon: Phone, text: '+91 97898 90769' },
  { Icon: Mail, text: 'reachgroups26@gmail.com' },
  { Icon: Globe, text: 'www.reachgroups.co.in' },
  { Icon: Clock, text: '09:00 AM – 09:00 PM (All Days)' },
];

const Footer: React.FC = () => {
  const isMobile = useIsMobile();

  // Mobile spacing is noticeably tighter throughout — that plus putting
  // Quick Links and Our Verticals side-by-side (instead of each stacking
  // full-width, one after another) is what actually shortens the footer.
  const linkMarginBottom = isMobile ? 8 : 12;
  const linkFontSize = isMobile ? 13 : 14;
  const headingMarginBottom = isMobile ? 12 : 20;

  const QuickLinksCol = (
    <motion.div variants={colItem}>
      <h4 style={{ color: '#fff', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 15, marginBottom: headingMarginBottom }}>Quick Links</h4>
      {QUICK_LINKS.map(l => (
        <Link key={l.to} to={l.to} className="footer-link" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#94a3b8', fontSize: linkFontSize, marginBottom: linkMarginBottom, fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}>
          <ArrowRight size={13} className="footer-link-arrow" /> <span className="footer-link-label">{l.label}</span>
        </Link>
      ))}
    </motion.div>
  );

  const VerticalsCol = (
    <motion.div variants={colItem}>
      <h4 style={{ color: '#fff', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 15, marginBottom: headingMarginBottom }}>Our Verticals</h4>
      {verticals.map(v => {
        const Icon = verticalIcons[v.id];
        return (
          <a key={v.id} href={urlForVertical(v)} target="_blank" rel="noopener noreferrer" className="footer-link" style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: linkFontSize, marginBottom: linkMarginBottom, fontFamily: 'Poppins, sans-serif', fontWeight: 500, textDecoration: 'none' }}>
            {Icon && <Icon size={14} className="footer-link-arrow" />} <span className="footer-link-label" style={{ whiteSpace: isMobile ? 'normal' : 'nowrap', lineHeight: 1.3 }}>{v.name}</span>
          </a>
        );
      })}
    </motion.div>
  );

  const ContactCol = (
    <motion.div variants={colItem}>
      <h4 style={{ color: '#fff', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 15, marginBottom: headingMarginBottom }}>Contact Us</h4>
      {CONTACT_ITEMS.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, marginBottom: isMobile ? 10 : 14 }}>
          <item.Icon size={16} style={{ flexShrink: 0, marginTop: 2, color: '#0B7078' }} />
          <span style={{ fontSize: linkFontSize, color: '#94a3b8', lineHeight: 1.6 }}>{item.text}</span>
        </div>
      ))}
    </motion.div>
  );

  const BrandCol = (
    <motion.div variants={colItem}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isMobile ? 12 : 16 }}>
        <motion.img
          src={stock.mainlogo} alt="Reach Groups Logo" width={isMobile ? 46 : 54} height={isMobile ? 38 : 44}
          whileHover={{ scale: 1.08, rotate: -4 }} transition={{ type: 'spring', stiffness: 300, damping: 15 }}
          style={{ filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.35))' }}
        />
        <div>
          <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: 16, color: '#fff' }}>REACH GROUPS</div>
          <div style={{ fontSize: 10, color: '#94a3b8', letterSpacing: '1.5px' }}>ONE REACH ENDLESS CARE</div>
        </div>
      </div>
      <p style={{ fontSize: linkFontSize, lineHeight: 1.8, color: '#94a3b8', marginBottom: isMobile ? 14 : 20 }}>
        An integrated healthcare ecosystem serving Chennai with 9+ verticals spanning diagnostics, clinics, home care, pharma, education and sports.
      </p>
      <div style={{ display: 'flex', gap: 12 }}>
        {[FacebookIcon].map((Icon, i) => (
          <motion.a
            key={i} href="https://www.facebook.com/share/1J2ScrkhEN/" target="_blank" rel="noopener noreferrer" aria-label="Social link"
            whileHover={{ scale: 1.12, y: -3 }} whileTap={{ scale: 0.94 }}
            style={{
              width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1',
              transition: 'background 0.25s, color 0.25s',
            }}
            onMouseEnter={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background = '#005B66'; t.style.color = '#fff'; }}
            onMouseLeave={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background = 'rgba(255,255,255,0.08)'; t.style.color = '#cbd5e1'; }}
          ><Icon size={16} /></motion.a>
        ))}
                {[InstagramIcon].map((Icon, i) => (
          <motion.a
            key={i} href="https://www.instagram.com/reachgroups26?igsi=b2czNTF2cnVwZW0" target="_blank" rel="noopener noreferrer" aria-label="Social link"
            whileHover={{ scale: 1.12, y: -3 }} whileTap={{ scale: 0.94 }}
            style={{
              width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1',
              transition: 'background 0.25s, color 0.25s',
            }}
            onMouseEnter={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background = '#005B66'; t.style.color = '#fff'; }}
            onMouseLeave={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background = 'rgba(255,255,255,0.08)'; t.style.color = '#cbd5e1'; }}
          ><Icon size={16} /></motion.a>
                ))}
                {[TwitterIcon].map((Icon, i) => (
          <motion.a
            key={i} href="https://x.com/reachgroups" target="_blank" rel="noopener noreferrer" aria-label="Social link"
            whileHover={{ scale: 1.12, y: -3 }} whileTap={{ scale: 0.94 }}
            style={{
              width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1',
              transition: 'background 0.25s, color 0.25s',
            }}
            onMouseEnter={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background = '#005B66'; t.style.color = '#fff'; }}
            onMouseLeave={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background = 'rgba(255,255,255,0.08)'; t.style.color = '#cbd5e1'; }}
          ><Icon size={16} /></motion.a>
                ))}
      </div>
    </motion.div>
  );

  return (
    <footer style={{ background: 'linear-gradient(180deg, #0b3e44 0%, #0a2a2e 45%, #061c1f 100%)', color: '#cbd5e1', paddingTop: 0, position: 'relative', overflow: 'hidden' }}>
      {/* Thin accent line along the very top, matching the navbar */}
      <div style={{ height: 3, background: 'linear-gradient(90deg, #D62828, #f04e4e, #D62828)', backgroundSize: '200% 100%', animation: 'footerAccentFlow 6s linear infinite' }} />

      <div style={{ position: 'absolute', top: -120, right: -120, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(214,40,40,0.10) 0%, transparent 70%)' }} />
      <div className="blob float" style={{ bottom: -140, left: -100, width: 320, height: 320, background: 'radial-gradient(circle, rgba(11,112,120,0.18) 0%, transparent 70%)' }} />

      <div className="container" style={{ position: 'relative', paddingTop: isMobile ? 36 : 64 }}>
        {isMobile ? (
          /* Mobile: Brand full-width, then Quick Links + Our Verticals as two
             side-by-side columns (instead of each stacking full-width one
             after another, which is what made the footer so tall), then
             Contact full-width. Same content, same links — just laid out to
             take less vertical space. */
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }} variants={colStagger}
            style={{ display: 'flex', flexDirection: 'column', gap: 28, paddingBottom: 32 }}
          >
            {BrandCol}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {QuickLinksCol}
              {VerticalsCol}
            </div>
            {ContactCol}
          </motion.div>
        ) : (
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }} variants={colStagger}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 40, paddingBottom: 48 }}
          >
            {BrandCol}
            {QuickLinksCol}
            {VerticalsCol}
            {ContactCol}
          </motion.div>
        )}

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: isMobile ? '16px 0' : '20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <p style={{ fontSize: 13, color: '#64748b' }}>© 2026 Reach Groups. All rights reserved.</p>
          {!isMobile && <p style={{ fontSize: 13, color: '#64748b' }}>Pillayar Koil Street, C. Pallavaram, Chennai – 600043</p>}
        </div>
      </div>

      <style>{`
        @keyframes footerAccentFlow { 0% { background-position: 0% 0; } 100% { background-position: -200% 0; } }
        .footer-link { transition: color 0.25s ease; }
        .footer-link:hover { color: #fff !important; }
        .footer-link-arrow { transition: transform 0.25s cubic-bezier(.22,1,.36,1); flex-shrink: 0; }
        .footer-link:hover .footer-link-arrow { transform: translateX(4px); }
        .footer-link-label { position: relative; }
        .footer-link-label::after {
          content: ''; position: absolute; left: 0; right: 0; bottom: -2px; height: 1px;
          background: currentColor; transform: scaleX(0); transform-origin: left; transition: transform 0.3s ease;
        }
        .footer-link:hover .footer-link-label::after { transform: scaleX(1); }
      `}</style>
    </footer>
  );
};

export default Footer;